// Overview — หน้า default ของแบรนด์
//
// ตัวชี้วัดที่เลือกมาโชว์ อ้างจากเกณฑ์ที่ใช้กันในวงการ (ดู docs/OVERVIEW-METRICS.md):
//   KOL       → total views + จำนวน KOL + engagement rate (reach/engagement ก่อน ค่อยดูต้นทุน)
//   Biddable  → spend, reach, CPM, CTR (ประสิทธิภาพต้นทุน ไม่ใช่แค่ยอดใช้เงิน)
//   Inbox     → จำนวนข้อความ, first response time, SLA ภายใน 1 ชม., ห้องค้างเกิน 24 ชม.
// จำกัดจำนวนตัวเลขบนจอ ไม่ยัดทุกอย่าง — เกิน ~12 ตัวคนอ่านจะเลิกอ่าน
import { useState } from "react";
import type { Brand } from "@contracts/api";
import { AdsBlock } from "../blocks/ads-block";
import { CampaignListBlock } from "../blocks/campaign-list-block";
import { ContentBlock } from "../blocks/content-block";
import { InboxBlock } from "../blocks/inbox-block";
import { KolBlock } from "../blocks/kol-block";
import { KolPostsBlock } from "../blocks/kol-posts-block";
import { RoomsBlock } from "../blocks/rooms-block";
import { ChatDrawer, type ChatTarget } from "../components/chat-drawer";
import { RangePills } from "../components/range-pills";

const INBOX_RANGES = [
  { v: 1, label: "วันนี้" },
  { v: 7, label: "7 วัน" },
  { v: 15, label: "15 วัน" },
  { v: 30, label: "30 วัน" },
];
const CONTENT_RANGES = [
  { v: 7, label: "7 วัน" },
  { v: 15, label: "15 วัน" },
  { v: 30, label: "30 วัน" },
];
const KOL_RANGES = [
  { v: 7, label: "7 วัน" },
  { v: 15, label: "15 วัน" },
  { v: 30, label: "30 วัน" },
  { v: 90, label: "90 วัน" },
  { v: 0, label: "ทั้งหมด" },
];
// ค่าที่ Biddable รองรับ — เป็น preset ไม่ใช่จำนวนวัน
const ADS_RANGES = [
  { v: "last_7d", label: "7 วัน" },
  { v: "last_14d", label: "14 วัน" },
  { v: "last_30d", label: "30 วัน" },
  { v: "this_month", label: "เดือนนี้" },
  { v: "last_month", label: "เดือนก่อน" },
];

export function OverviewPage({ brand }: { brand: Brand }) {
  // ช่วงเวลาแยกต่อบล็อก — กดของบล็อกหนึ่งไม่ยิง request ของบล็อกอื่น
  const [inboxDays, setInboxDays] = useState(1);
  const [contentDays, setContentDays] = useState(7);
  const [kolDays, setKolDays] = useState(7);
  const [adsPreset, setAdsPreset] = useState("last_7d");
  const [chat, setChat] = useState<ChatTarget | null>(null);

  return (
    <>
      <div className="content-head">
        <div>
          <div className="breadcrumb">{brand.name} / Overview</div>
          <h1>Overview</h1>
        </div>
      </div>

      <section className="ov-block">
        <h3 className="ov-h">แคมเปญ</h3>
        <CampaignListBlock brand={brand} />
      </section>

      <section className="ov-block">
        <div className="ov-block-head">
          <h3 className="ov-h">Inbox</h3>
          <RangePills options={INBOX_RANGES} value={inboxDays} onPick={setInboxDays} />
        </div>
        <InboxBlock brand={brand} days={inboxDays} />
        <RoomsBlock
          brand={brand}
          onOpen={(c) => setChat({ convId: c.id, pageId: c.pageId, customerName: c.customerName })}
        />
      </section>

      <section className="ov-block">
        <div className="ov-block-head">
          <h3 className="ov-h">Content</h3>
          <RangePills options={CONTENT_RANGES} value={contentDays} onPick={setContentDays} />
        </div>
        <ContentBlock brand={brand} days={contentDays} />
      </section>

      <section className="ov-block">
        <div className="ov-block-head">
          <h3 className="ov-h">KOL</h3>
          <RangePills options={KOL_RANGES} value={kolDays} onPick={setKolDays} />
        </div>
        <KolBlock brand={brand} days={kolDays} />
        <KolPostsBlock brand={brand} days={kolDays} />
      </section>

      <section className="ov-block">
        <div className="ov-block-head">
          <h3 className="ov-h">Ads</h3>
          <RangePills options={ADS_RANGES} value={adsPreset} onPick={setAdsPreset} />
        </div>
        <AdsBlock brand={brand} preset={adsPreset} />
      </section>

      <ChatDrawer brandId={brand.id} target={chat} onClose={() => setChat(null)} />
    </>
  );
}
